import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/AuthContext';
import { useClientMessagingWebSocket } from '../../../hooks/useClientMessagingWebSocket';
import { messagingService } from '../../../services/messaging.service';
import { theme } from '../../../theme';
import MessagingConversationList from './shared/MessagingConversationList';
import MessagingThreadView from './shared/MessagingThreadView';

const ClientMessagesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const onSocketMessage = useCallback((event) => {
    if (event.type !== 'new_message' || !event.message) return;
    if (
      Number(selectedIdRef.current)
      === Number(event.message.conversation_id)
    ) {
      messagingService.markClientConversationRead(
        event.message.conversation_id,
      ).catch(() => {});
      setConversations((items) => items.map((item) => (
        Number(item.id) === Number(event.message.conversation_id)
          ? { ...item, unread_count: 0 }
          : item
      )));
    }
    setDetail((current) => {
      if (!current || Number(current.id) !== Number(event.message.conversation_id)) return current;
      if (current.messages?.some((item) => Number(item.id) === Number(event.message.id))) return current;
      return { ...current, messages: [...(current.messages || []), event.message] };
    });
  }, []);
  const { sendJson } = useClientMessagingWebSocket({
    token: user?.token || localStorage.getItem('access_token'),
    enabled: true,
    onMessage: onSocketMessage,
  });

  useEffect(() => {
    let active = true;
    messagingService.listClientConversations().then((response) => {
      if (!active) return;
      const items = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setConversations(items);
      const requestedId = Number(new URLSearchParams(window.location.search).get('conversation'));
      setSelectedId((current) => current || items.find((item) => Number(item.id) === requestedId)?.id || items[0]?.id || null);
    }).catch(() => active && setError(t('client_messaging.load_error', 'Impossible de charger les discussions.')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [t]);

  useEffect(() => {
    if (!selectedId) return;
    let active = true;
    setDetail((current) => (
      Number(current?.id) === Number(selectedId) ? current : null
    ));
    setError('');
    messagingService.retrieveClientConversation(selectedId).then((response) => {
      if (!active) return;
      setDetail(response.data);
      messagingService.markClientConversationRead(selectedId).catch(() => {});
      sendJson({ action: 'join_conversation', conversation_id: selectedId });
    }).catch(() => active && setError(t('client_messaging.load_error', 'Impossible de charger la discussion.')));
    return () => { active = false; };
  }, [selectedId, sendJson, t]);

  const send = async () => {
    const content = message.trim();
    if (!content || !selectedId || sending || detail?.status === 'closed') {
      return;
    }
    setSending(true);
    setError('');
    try {
      const response = await messagingService.sendClientConversationMessage(selectedId, content);
      setDetail(response.data);
      setMessage('');
      const lastMessage = response.data?.messages?.[
        response.data.messages.length - 1
      ];
      setConversations((items) => items.map((item) => (
        Number(item.id) === Number(selectedId)
          ? {
            ...item,
            ...response.data,
            last_message: lastMessage || item.last_message,
            unread_count: 0,
          }
          : item
      )));
    } catch (_error) {
      setError(t('client_messaging.send_error', 'Impossible d’envoyer le message.'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.colors.primary[800] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1220, mx: 'auto', px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 2.25 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: theme.colors.secondary[950],
            letterSpacing: '-0.02em',
          }}
        >
          {t('client_messaging.discussions', 'Discussions')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: theme.colors.grey[600] }}
        >
          Retrouvez ici tous vos échanges avec les salons.
        </Typography>
      </Box>

      {error && !detail && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr)' },
          height: {
            xs: 'calc(100dvh - 190px)',
            md: 'min(720px, calc(100vh - 190px))',
          },
          minHeight: { xs: 500, md: 560 },
          overflow: 'hidden',
          borderRadius: { xs: '20px', md: '24px' },
          border: `1px solid ${theme.colors.secondary[200]}`,
          backgroundColor: theme.colors.secondary[50],
          boxShadow: '0 22px 55px rgba(67, 44, 23, 0.10)',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: { xs: detail ? 'none' : 'block', md: 'block' },
            borderRight: { md: `1px solid ${theme.colors.secondary[200]}` },
          }}
        >
          <MessagingConversationList
            conversations={conversations}
            selectedId={selectedId}
            query={query}
            onQueryChange={setQuery}
            onSelect={(conversation) => setSelectedId(conversation.id)}
            getIdentity={(conversation) => (
              conversation.salon?.name || 'Salon'
            )}
            getPreview={(conversation) => (
              conversation.last_message?.content
              || conversation.subject
              || t(
                `client_messaging.kind_${conversation.kind}`,
                conversation.kind || 'Discussion',
              )
            )}
            emptyTitle="Aucune discussion pour le moment"
            emptyDescription="Vos échanges avec les salons apparaîtront ici."
          />
        </Box>
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: { xs: detail ? 'block' : 'none', md: 'block' },
          }}
        >
          <MessagingThreadView
            identity={detail?.salon?.name}
            subtitle={
              detail?.subject
              || (detail?.kind
                ? t(
                  `client_messaging.kind_${detail.kind}`,
                  detail.kind,
                )
                : '')
            }
            status={detail?.status}
            messages={detail?.messages || []}
            ownSenderType="client"
            draft={message}
            onDraftChange={setMessage}
            onSend={send}
            sending={sending}
            error={detail ? error : ''}
            onBack={() => {
              setSelectedId(null);
              setDetail(null);
              setError('');
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ClientMessagesPage;
